import { AdminApiError, adminRequest } from '../admin-api';
import { isBookLanguageCode } from '@living-library/contracts';
export interface Metadata {
  title: string;
  subtitle: string | null;
  authors: string[];
  authorUnknown: boolean;
  language: string;
  isbn: string | null;
  publisher: string | null;
  publishedYear: number | null;
  pageCount: number | null;
  binding: 'paperback' | 'hardcover' | 'ebook' | 'other' | null;
  description: string | null;
}
export interface Cover {
  policy: 'automatic' | 'moderator' | 'none';
  url?: string;
  rightsNote?: string;
}
export interface Book extends Metadata {
  id: string;
  metadataVersion: number;
  catalogueVisibility: 'visible' | 'hidden';
  coverPolicy: Cover['policy'];
  coverImageUrl: string | null;
  coverRightsNote?: string | null;
  source: string | null;
  sourceId?: string | null;
  sourceUrl?: string | null;
}
export interface Submission {
  id: string;
  bookId: string;
  status: 'pending' | 'changes_requested' | 'declined' | 'withdrawn' | 'published' | 'matched';
  version: number;
  metadata: Metadata;
  moderatorMessage: string | null;
  matchedBookId: string | null;
  publishedBookId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface Report {
  id: string;
  bookId: string | null;
  bookTitle?: string;
  reason: 'metadata' | 'duplicate' | 'wrong_edition' | 'inappropriate' | 'cover' | 'rights';
  details: string;
  targetBookId: string | null;
  status: 'pending' | 'resolved' | 'dismissed';
  version: number;
  moderatorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  issues?: ReportIssue[];
  evidence?: string;
}
export interface ReportIssue {
  id: string;
  target: 'cover' | 'description' | 'book';
  reasons: string[];
  details: string;
  status: 'pending' | 'dismissed' | 'confirmed' | 'replaced' | 'resolved';
  versionKey: string | null;
  contentVersion: number | null;
  state: 'visible' | 'held' | 'suppressed' | null;
  safetyHold: boolean;
  moderatorMessage?: string | null;
  updatedAt?: string;
  content?: { url?: string; description?: string; [key: string]: unknown } | null;
  identity?: Record<string, unknown> | null;
  replacement?: { url?: string; description?: string; rightsNote?: string } | null;
  relatedReportCount?: number;
  relatedReportsTruncated?: boolean;
  relatedReports?: {
    id: string;
    status: string;
    reasons: string[];
    details?: string;
    createdAt: string;
  }[];
}
export interface Detail {
  submission: Submission;
  moderatorCover?: Cover | null;
  currentBook: Book | null;
  candidates: Book[];
}
export type Request = <T>(path: string, body?: unknown) => Promise<T>;
export const makeRequest =
  (token: () => Promise<string>, denied: () => void): Request =>
  async <T>(path: string, body?: unknown) => {
    try {
      return (await adminRequest(
        token,
        `/admin/catalogue-contributions${path}`,
        body instanceof Blob
          ? {
              method: 'PUT',
              body,
              headers: { 'Content-Type': body.type },
              signal: AbortSignal.timeout(30_000),
            }
          : body
            ? { method: 'POST', body: JSON.stringify(body) }
            : {},
      )) as T;
    } catch (error) {
      if (error instanceof AdminApiError && error.status === 403) denied();
      throw error;
    }
  };
export const conflict = (error: unknown) => error instanceof AdminApiError && error.status === 409;
export const errorMessage = (error: unknown) =>
  conflict(error)
    ? 'This record changed. Reload it and review the latest version before deciding.'
    : 'The request could not be completed. Your edits are retained; retry or reload.';
export function coverReady(cover: Cover, loadedUrl: string | null) {
  if (cover.policy !== 'moderator') return true;
  try {
    const url = new URL(cover.url ?? '');
    return url.protocol === 'https:' && !url.username && !url.password && loadedUrl === cover.url;
  } catch {
    return false;
  }
}
export function identityChanged(original: Metadata, edited: Metadata) {
  const normalize = (v: unknown): string =>
    Array.isArray(v)
      ? v.map(normalize).join('|')
      : String(v ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, ' ')
          .toLocaleLowerCase();
  return (
    [
      'title',
      'authors',
      'authorUnknown',
      'isbn',
      'language',
      'binding',
      'publishedYear',
      'publisher',
    ] as const
  ).some((key) => normalize(original[key]) !== normalize(edited[key]));
}
export function validMetadata(data: Metadata) {
  return Boolean(
    data.title.trim() &&
    isBookLanguageCode(data.language?.toLowerCase()) &&
    (data.authorUnknown || data.authors.some((a) => a.trim())) &&
    (data.pageCount === null || (Number.isSafeInteger(data.pageCount) && data.pageCount > 0)) &&
    (data.publishedYear === null ||
      (Number.isSafeInteger(data.publishedYear) && data.publishedYear > 0)),
  );
}
export function groupReports(reports: Report[]) {
  const groups = new Map<string, Report[]>();
  for (const report of reports) {
    // Unsaved editions have no book row. Identical content can appear on
    // unrelated books; keep their contexts separate and link them per issue.
    const key = report.bookId ?? report.id;
    groups.set(key, [...(groups.get(key) ?? []), report]);
  }
  return [...groups.entries()];
}
// Reuse the idempotency key after an uncertain response, but never for an edited decision.
export function decisionSender(request: Request, uuid: () => string = () => crypto.randomUUID()) {
  let previous = '',
    clientRequestId = '';
  return <T>(path: string, body: Record<string, unknown>) => {
    const fingerprint = JSON.stringify({ path, body });
    if (fingerprint !== previous) {
      previous = fingerprint;
      clientRequestId = uuid();
    }
    return request<T>(path, { ...body, clientRequestId });
  };
}

export function editableMetadata(book: Metadata): Metadata {
  const {
    title,
    subtitle,
    authors,
    authorUnknown,
    language,
    isbn,
    publisher,
    publishedYear,
    pageCount,
    binding,
    description,
  } = book;
  return {
    title,
    subtitle,
    authors,
    authorUnknown,
    language,
    isbn,
    publisher,
    publishedYear,
    pageCount,
    binding,
    description,
  };
}

export function sharedCorrectionDraft(original: Metadata, edited: Metadata, cover?: Cover) {
  const metadataChanged =
    JSON.stringify(editableMetadata(original)) !== JSON.stringify(editableMetadata(edited));
  return {
    canSave:
      (metadataChanged || cover !== undefined) && (!metadataChanged || validMetadata(edited)),
    payload: {
      ...(metadataChanged ? { metadata: editableMetadata(edited) } : {}),
      ...(cover ? { cover } : {}),
    },
  };
}
